'use client';

import type { Plan } from '@/core/domain';
import {
  formatPlanPriceBrlFromCents,
  PLAN_BILLING_PERIOD_LABELS,
  PLAN_BILLING_PERIODS,
  PLAN_KIND_LABELS,
} from '@/core/domain';
import { Badge } from '@/core/presentation/components/ui/Badge';
import { Button } from '@/core/presentation/components/ui/Button';
import { Switch } from '@/core/presentation/components/ui/Switch';
import { Edit, Hash, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';

export interface PlanAdminCardProps {
  plan: Plan;
  busy: boolean;
  onEdit: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  onDelete: (id: string) => void;
}

function stripeSummary(plan: Plan, period: (typeof PLAN_BILLING_PERIODS)[number]): string {
  const variant = plan.billing_variants?.[period];
  if (!variant) return 'Não configurado';
  const id = variant.stripe.stripe_price_id;
  if (id) return id;
  const key = variant.stripe.stripe_price_lookup_key;
  if (key) return `lookup: ${key}`;
  return 'Stripe pendente';
}

export function PlanAdminCard({ plan, busy, onEdit, onToggleActive, onDelete }: PlanAdminCardProps) {
  const handleDelete = () => {
    const ok = window.confirm(`Excluir "${plan.title}"? Esta ação não pode ser desfeita.`);
    if (ok) onDelete(plan.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-admin-border p-5 shadow-sm flex flex-col"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-admin-text truncate">{plan.title}</h3>
            <Badge
              variant="outline"
              className={
                plan.is_active
                  ? 'border-admin-success/40 bg-admin-success/10 text-admin-success'
                  : 'border-admin-text-secondary/30 bg-admin-text-secondary/10 text-admin-text-secondary'
              }
            >
              {plan.is_active ? 'Ativo' : 'Inativo'}
            </Badge>
            <Badge
              variant="outline"
              className={
                plan.kind === 'free'
                  ? 'border-cyan/40 bg-cyan/10 text-admin-cyan-dark'
                  : 'border-admin-navy/30 bg-admin-navy/5 text-admin-navy'
              }
            >
              {PLAN_KIND_LABELS[plan.kind]}
            </Badge>
          </div>
        </div>
        <Switch
          checked={plan.is_active}
          onCheckedChange={(v) => onToggleActive(plan.id, v)}
          disabled={busy}
          aria-label={plan.is_active ? 'Desativar plano' : 'Ativar plano'}
        />
      </div>

      <div className="space-y-2 text-sm text-admin-text mb-4 flex-1">
        {plan.kind === 'free' ?
          <div className="space-y-1 text-admin-text-secondary leading-relaxed">
            <p>
              Limite de serviços:{' '}
              <span className="font-semibold text-admin-text">
                {plan.free_limits?.max_services != null ?
                  plan.free_limits.max_services
                : '—'}
              </span>
            </p>
            <p>Sem Stripe — ativação direta na app.</p>
          </div>
        : (
          <ul className="space-y-2">
            {PLAN_BILLING_PERIODS.map((period) => {
              const variant = plan.billing_variants?.[period];
              return (
                <li
                  key={period}
                  className="rounded-lg border border-admin-border/80 bg-admin-bg-block px-3 py-2"
                >
                  <p className="font-medium text-admin-text">
                    {PLAN_BILLING_PERIOD_LABELS[period]}
                    {variant ?
                      <span className="ml-2 font-bold text-admin-navy">
                        {formatPlanPriceBrlFromCents(variant.price_brl_cents)}
                      </span>
                    : null}
                  </p>
                  <p
                    className="mt-0.5 text-xs font-mono text-admin-text-secondary truncate"
                    title={stripeSummary(plan, period)}
                  >
                    {stripeSummary(plan, period)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex items-center gap-2 text-admin-text-secondary pt-1">
          <Hash className="h-4 w-4 shrink-0" />
          <span>
            Ordem <span className="font-medium text-admin-text">{plan.sort_order}</span>
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-auto">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 min-w-[7rem] border-admin-border text-admin-text hover:bg-admin-bg"
          disabled={busy}
          onClick={() => onEdit(plan.id)}
        >
          <Edit className="h-3.5 w-3.5 mr-1" />
          Editar
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="border-destructive/40 text-destructive hover:bg-destructive/10"
          disabled={busy}
          onClick={handleDelete}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" />
          Excluir
        </Button>
      </div>
    </motion.div>
  );
}
